import React, { useState, useEffect } from 'react'
import {
  Column,
  Table,
  AutoSizer,
  defaultTableRowRenderer,
  defaultTableCellRenderer,
  defaultTableHeaderRowRenderer,
  defaultTableHeaderRenderer
} from 'react-virtualized'
import 'react-virtualized/styles.css'
import styled from '@emotion/styled'

import { TableRow, TableCell, TableHeader, TableHead } from 'Components/Table'
import { H2, LabelWithTitle } from 'Components/shared/Typography'
import { ClickableLink } from 'Components/shared/Link'
import { createModal } from 'Components/Modal'
import AlertIcon from 'Components/shared/AlertIcon'
import Tag from 'Components/shared/Tag'
import SelectStatus, { statusOptions, allOptionStatus } from 'Components/shared/SelectStatus'
import SelectOwner, { ownerOptions, allOptionOwner } from 'Components/shared/SelectOwner'
import SelectAlert, { allOptionAlert } from 'Components/shared/SelectAlert'

import { Flex, Grid } from 'lese'
import { SORTINGTYPES, colors } from 'Constants'
import { convertToReadableDate } from 'Helpers'
import { toastWithErrorMessage } from 'Helpers/toast'

import EncountersService from 'Services/encounters'
import UserStore from 'Store/user'

import EncounterView from './EncounterView'
import ContactPatient, { MessageImage } from '../ContactPatient'

const EncounterListTableHeight = 500
const EncounterListTableContainer = styled(Flex)`
  flex: 1;
  height: ${EncounterListTableHeight}px;
`

export default ({ tid, tcode, deleted, journeyName, groupName, medicalRef, officeId }) => {
  const [encounters, setEncounters] = useState([])
  const [initialCount, setInitialCount] = useState(0)
  const [owners, setOwners] = useState([])
  const [filter, setFilter] = useState({
    alert: allOptionAlert,
    status: allOptionStatus,
    owner: allOptionOwner
  })

  const getEncountersByTcode = async ({ init, alert, status, owner, startIdx }) => {
    const { virtualOffices } = UserStore.get()
    // set the filter in state
    setFilter({ alert, status, owner })

    let filteringParams = {}
    if (alert.value !== allOptionAlert.value) {
      filteringParams = { type: 'ALERTLEVEL', alertLevel: alert.value }
    }

    const keys = ['T-Code']
    const values = [tcode]

    if (status.value !== allOptionStatus.value) {
      keys.push('Status')
      values.push(status.value)
    }

    if (owner.value !== allOptionOwner.value) {
      keys.push('Owner')
      values.push(owner.value)
    }

    const searchParams = {
      searchParam: { keys, values }
    }

    const sortingParams = {
      type: SORTINGTYPES.DATE,
      reverse: false
    }

    const encountersTcode = await EncountersService.list({
      virtualOfficeId: virtualOffices[0]?.id,
      sortingParams,
      searchParams,
      filteringParams,
      skip: startIdx
    }).catch(toastWithErrorMessage('Failed to load new encounters'))

    if (init) {
      setInitialCount(encountersTcode.length)
    }

    return encountersTcode.map(item => {
      return {
        encounterId: item.id,
        alert: item.alertEnum,
        bio: item.bio,
        created: item.sentDate,
        status: item.review.status,
        owner: item.review.userId || null,
        PMHID: item.PMHID,
        token: item.token,
        isExternal: item.isExternal,
        inactive: item.inactive
      }
    })
  }

  useEffect(async () => {
    const options = await ownerOptions([officeId])
    setOwners(options)

    const newEncounters = await getEncountersByTcode({ init: true, ...filter, startIdx: 0 })
    setEncounters(newEncounters)
  }, [tid])

  const loadMore = async () => {
    const nextEncounters = await getEncountersByTcode({
      init: false,
      ...filter,
      startIdx: encounters.length
    })
    setEncounters(encounters.concat(nextEncounters))
  }

  const onFilterChange = async (e, dataKey) => {
    const newEncounters = await getEncountersByTcode({
      init: false,
      ...filter,
      ...{ [dataKey]: e },
      startIdx: 0
    })
    setEncounters(newEncounters)
  }

  const onStatusChange = async (encounterId, oldStatus, newStatus) => {
    const updateStatus = await EncountersService.updateStatus({
      encounterId,
      oldStatus,
      newStatus
    }).catch(toastWithErrorMessage('Failed to update encounter status'))
    if (!updateStatus) return

    // update encounter state with new status
    const newEncounters = encounters.map(item => {
      if (item.encounterId === encounterId) item.status = newStatus
      return item
    })

    setEncounters(newEncounters)
  }

  const onOwnerChange = async (encounterId, oldOwner, newOwner) => {
    const updateOwner = await EncountersService.updateOwner({
      encounterId,
      oldOwner,
      newOwner
    }).catch(toastWithErrorMessage('Failed to update encounter status'))
    if (!updateOwner) return

    // update encounter state with new owner
    const newEncounters = encounters.map(item => {
      if (item.encounterId === encounterId) item.owner = newOwner
      return item
    })

    setEncounters(newEncounters)
  }

  const headerRowRenderer = props => {
    return <TableHead style={{ padding: 0 }}>{defaultTableHeaderRowRenderer(props)}</TableHead>
  }

  const headerRenderer = props => {
    const { dataKey } = props
    let filterContent

    switch (dataKey) {
      case 'alert':
        filterContent = (
          <SelectAlert
            includeAll
            menuPortalTarget={document.body}
            value={filter.alert}
            onChange={e => {
              onFilterChange(e, dataKey)
            }}
          />
        )
        break
      case 'status':
        filterContent = (
          <SelectStatus
            includeAll
            menuPortalTarget={document.body}
            value={filter.status}
            onChange={e => {
              onFilterChange(e, dataKey)
            }}
          />
        )
        break
      case 'owner':
        filterContent = (
          <SelectOwner
            includeAll
            options={owners}
            menuPortalTarget={document.body}
            value={filter.owner}
            onChange={e => {
              onFilterChange(e, dataKey)
            }}
          />
        )
        break
      default:
        filterContent = ''
    }

    return (
      <TableHeader>
        <div>{defaultTableHeaderRenderer(props)}</div>
        <div style={{ height: 40, paddingTop: 5 }}>{filterContent}</div>
      </TableHeader>
    )
  }

  const rowRenderer = props => {
    return <TableRow>{defaultTableRowRenderer(props)}</TableRow>
  }

  const cellRenderer = props => {
    const { dataKey, cellData, rowData } = props
    let content

    switch (dataKey) {
      case 'alert':
        content = (
          <AlertIcon
            alertEnum={cellData}
            inactiveType={rowData.inactive.type}
            inactiveDays={rowData.inactive.days}
          />
        )
        break
      case 'bio':
        content = cellData || '-'
        break
      case 'created':
        content = (
          <ClickableLink
            onClick={() => {
              createModal(
                <EncounterView
                  id={rowData.encounterId}
                  PMHID={rowData.PMHID}
                  token={rowData.token}
                  isExternal={rowData.isExternal}
                />
              )
            }}
          >
            {convertToReadableDate(cellData)}
          </ClickableLink>
        )
        break
      case 'status': {
        const selectedStatus = statusOptions.filter(item => item.value === cellData)
        content = (
          <SelectStatus
            menuPortalTarget={document.body}
            value={selectedStatus}
            onChange={item => {
              if (item.value === cellData) return
              onStatusChange(rowData.encounterId, cellData, item.value)
            }}
          />
        )
        break
      }
      case 'owner': {
        const selectedOwner = owners.filter(item => item.value === cellData)
        content = (
          <SelectOwner
            menuPortalTarget={document.body}
            options={owners}
            value={selectedOwner}
            onChange={item => {
              if (item.value === cellData) return
              onOwnerChange(rowData.encounterId, cellData, item.value)
            }}
          />
        )
        break
      }
      default:
        content = defaultTableCellRenderer(props)
    }
    return <TableCell>{content}</TableCell>
  }

  return (
    <>
      <Grid columns="20% auto 10%">
        <Flex column>
          <H2>{tcode}</H2>
          <Tag isActive={deleted === 'N'} text={deleted === 'N' ? 'Active' : 'Archived'} />
        </Flex>
        <Grid rows="1fr 1fr 1fr" gap="5px" style={{ paddingLeft: 10, paddingRight: 10 }} yAlign>
          <LabelWithTitle title="Pt#">{medicalRef}</LabelWithTitle>
          <LabelWithTitle title="Journey Name">{journeyName}</LabelWithTitle>
          <LabelWithTitle title="Group Name">{groupName}</LabelWithTitle>
        </Grid>
        <Flex column>
          <MessageImage
            src="/envelope.svg"
            title="Direct message to patient"
            onClick={() => {
              createModal(<ContactPatient tCode={tcode} />)
            }}
          >
            Contact Patient
          </MessageImage>
        </Flex>
      </Grid>
      {initialCount > 0 && (
        <EncounterListTableContainer>
          <AutoSizer>
            {({ width }) => (
              <Table
                rowCount={encounters.length}
                width={width}
                height={EncounterListTableHeight}
                headerHeight={100}
                headerRowRenderer={headerRowRenderer}
                rowRenderer={rowRenderer}
                rowHeight={50}
                rowGetter={({ index }) => encounters[index]}
                style={{
                  border: 'solid 1px',
                  backgroundColor: colors.background700,
                  width,
                  paddingRight: encounters.length > 8 ? 15 : 0
                }}
                overscanRowCount={20}
                onScroll={({ clientHeight, scrollHeight, scrollTop }) => {
                  if (scrollHeight < clientHeight + scrollTop + 1000) loadMore()
                }}
              >
                <Column
                  label="Alert"
                  dataKey="alert"
                  width={width / 6}
                  headerRenderer={headerRenderer}
                  cellRenderer={cellRenderer}
                />
                <Column
                  label="Bio"
                  dataKey="bio"
                  width={width / 4.5}
                  headerRenderer={headerRenderer}
                  cellRenderer={cellRenderer}
                />
                <Column
                  label="Created"
                  dataKey="created"
                  width={width / 4.5}
                  headerRenderer={headerRenderer}
                  cellRenderer={cellRenderer}
                />
                <Column
                  label="Status"
                  dataKey="status"
                  width={width / 4.5}
                  headerRenderer={headerRenderer}
                  cellRenderer={cellRenderer}
                />
                <Column
                  label="Owner"
                  dataKey="owner"
                  width={width / 4.5}
                  headerRenderer={headerRenderer}
                  cellRenderer={cellRenderer}
                />
              </Table>
            )}
          </AutoSizer>
        </EncounterListTableContainer>
      )}
    </>
  )
}
